import { useMemo, useState } from "react";
import { TrendingUp, Wallet, LineChart } from "lucide-react";

const RATE = 0.12;
const AMOUNTS = [5000, 10000, 25000];
const COMPARE_YEARS = 15;

function sipFv(monthly, years) {
  const i = RATE / 12;
  let balance = 0;
  for (let m = 0; m < years * 12; m += 1) balance = balance * (1 + i) + monthly;
  return balance;
}

function lumpFv(lumpsum, years) {
  return lumpsum * Math.pow(1 + RATE, years);
}

function formatINR(value) {
  if (value >= 1e7) return `₹${(value / 1e7).toFixed(2)} Cr`;
  if (value >= 1e5) return `₹${(value / 1e5).toFixed(1)} L`;
  return `₹${Math.round(value).toLocaleString("en-IN")}`;
}

export default function MarketInsights() {
  const [monthly, setMonthly] = useState(10000);
  const compare = useMemo(() => {
    const total = monthly * 12 * COMPARE_YEARS;
    return {
      invested: total,
      sipValue: sipFv(monthly, COMPARE_YEARS),
      lumpValue: lumpFv(total, COMPARE_YEARS),
    };
  }, [monthly]);
  const barMax = Math.max(compare.sipValue, compare.lumpValue);

  const stats = [
    { icon: TrendingUp, big: formatINR(sipFv(10000, 20)), label: "₹10,000/month SIP for 20 years", note: "You invest ₹24 L — compounding adds nearly ₹76 L more.", testid: "mkt-stat-sip" },
    { icon: Wallet, big: formatINR(lumpFv(100000, 20)), label: "₹1 L lumpsum left untouched 20 years", note: "One decision, two decades of quiet multiplication.", testid: "mkt-stat-lumpsum" },
    { icon: LineChart, big: "~13.5%", label: "Nifty 50 CAGR over the last 20 years", note: "Through the 2008 crash, Covid and every headline in between.", testid: "mkt-stat-nifty" },
  ];

  return (
    <section className="mkt-section" data-testid="mf-market-insights">
      <div className="section-wrap">
        <div className="section-heading-row">
          <div><p className="eyebrow light-eyebrow">CURRENT MARKET INSIGHTS</p><h2>Tiny money.<br /><em>Fortune-sized outcomes.</em></h2></div>
          <span className="section-index light-index">SIP / LUMPSUM</span>
        </div>
        <div className="mkt-grid">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <article className="mkt-card" key={stat.testid} data-testid={stat.testid}>
                <Icon size={22} className="mkt-icon" />
                <strong>{stat.big}</strong>
                <span className="mkt-label">{stat.label}</span>
                <p>{stat.note}</p>
              </article>
            );
          })}
        </div>
        <div className="mkt-compare" data-testid="sip-vs-lumpsum">
          <div className="mkt-compare-head">
            <h3>SIP vs Lumpsum <em>— same money, two paths.</em></h3>
            <div className="mkt-chips" data-testid="compare-amount-chips">
              {AMOUNTS.map((amt) => <button key={amt} className={monthly === amt ? "selected" : ""} onClick={() => setMonthly(amt)} data-testid={`compare-chip-${amt}`}>{formatINR(amt)}/mo</button>)}
            </div>
          </div>
          <p className="mkt-compare-sub">Investing {formatINR(compare.invested)} over {COMPARE_YEARS} years at an assumed 12% p.a.</p>
          <div className="mkt-bars" key={monthly}>
            <div className="mkt-bar-row" data-testid="compare-sip-bar">
              <div className="mkt-bar-head"><span>SIP · {formatINR(monthly)} every month</span><strong>{formatINR(compare.sipValue)}</strong></div>
              <div className="pulse-track mkt-track"><div className="pulse-fill mkt-fill" style={{ width: `${(compare.sipValue / barMax) * 100}%` }} /></div>
            </div>
            <div className="mkt-bar-row" data-testid="compare-lumpsum-bar">
              <div className="mkt-bar-head"><span>Lumpsum · {formatINR(compare.invested)} on day one</span><strong>{formatINR(compare.lumpValue)}</strong></div>
              <div className="pulse-track mkt-track"><div className="pulse-fill mkt-fill" style={{ width: `${(compare.lumpValue / barMax) * 100}%`, transitionDelay: "120ms" }} /></div>
            </div>
          </div>
          <p className="mkt-note">Lumpsum wins when you have the money ready; SIP wins on discipline and rupee-cost averaging. Illustrative at 12% p.a. — mutual fund returns are market-linked and not guaranteed.</p>
        </div>
      </div>
    </section>
  );
}
